/** Coerce Postgres NUMERIC / unknown values before calling .toFixed(). */
export function toNumber(value: unknown, fallback = 0): number {
  if (value == null || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Format a currency amount safely for UI chips/labels. */
export function formatEuro(value: unknown, digits = 2): string {
  return `€${toNumber(value).toFixed(digits)}`;
}

/**
 * Parse a user-typed decimal that may use "," or "." as the decimal separator
 * (common in EE/EU locales). Also tolerates mixed thousand separators.
 */
export function parseLocaleNumber(raw: unknown, fallback = NaN): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
  const s = String(raw ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(/€/g, '');
  if (!s) return fallback;

  let normalized = s;
  if (s.includes(',') && s.includes('.')) {
    // Whichever separator appears last is treated as the decimal mark.
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
      normalized = s.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = s.replace(/,/g, '');
    }
  } else if (s.includes(',')) {
    normalized = s.replace(',', '.');
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : fallback;
}

/** True if the string is an allowed in-progress decimal (e.g. "0,", "12.3"). */
export function isLocaleDecimalTyping(raw: string): boolean {
  return raw === '' || /^[0-9]*([.,][0-9]*)?$/.test(raw);
}

/** Display a number using comma as decimal separator for EU-friendly inputs. */
export function formatLocaleDecimal(value: unknown, digits?: number): string {
  const n = toNumber(value, 0);
  const fixed =
    typeof digits === 'number' ? n.toFixed(digits) : String(n);
  return fixed.replace('.', ',');
}
